import type { Router } from 'vue-router'
import { useAuthStore } from '@/plugins/w/auth/store/auth.store'
import { tokenHandshakeHandlers, type JsonRpcPayload } from './token-handshake-handlers'

export const TOKEN_HANDSHAKE_SESSION_KEY = 'mdx_token_handshake_active'
export const TOKEN_HANDSHAKE_COMPLETED_KEY = 'mdx_token_handshake_completed'
export const TOKEN_HANDSHAKE_ACCESS_SWITCH_KEY = 'mdx_token_handshake_access_switch'
const TOKEN_HANDSHAKE_PRESERVE_SESSION_KEY = 'mdx_token_handshake_preserve_session'

export function isTokenHandshakeActive(): boolean {
	return sessionStorage.getItem(TOKEN_HANDSHAKE_SESSION_KEY) === 'true'
}

export function isTokenHandshakeCompleted(): boolean {
	return sessionStorage.getItem(TOKEN_HANDSHAKE_COMPLETED_KEY) === 'true'
}

export function clearTokenHandshakeSession() {
	sessionStorage.removeItem(TOKEN_HANDSHAKE_SESSION_KEY)
}

export function clearTokenHandshakeCompleted() {
	sessionStorage.removeItem(TOKEN_HANDSHAKE_COMPLETED_KEY)
}

export function markTokenHandshakeCompleted() {
	clearTokenHandshakeSession()
	sessionStorage.setItem(TOKEN_HANDSHAKE_COMPLETED_KEY, 'true')
}

/** Pred reloadom po zmene ambulancie v iframe – session sa nesmie vymazat. */
export function markHostAccessSwitchReload() {
	sessionStorage.setItem(TOKEN_HANDSHAKE_ACCESS_SWITCH_KEY, 'true')
}

export function consumePreservedHostSession(): boolean {
	const preserve = sessionStorage.getItem(TOKEN_HANDSHAKE_PRESERVE_SESSION_KEY) === 'true'
	if (preserve) {
		sessionStorage.removeItem(TOKEN_HANDSHAKE_PRESERVE_SESSION_KEY)
	}
	return preserve
}

/** Host integrácia cez iframe (desktop klient / AIS posiela postMessage). */
export function isHostIntegrationContext(): boolean {
	return window.parent !== window
}

/**
 * Optional allowlist from env: VITE_TOKEN_HANDSHAKE_ORIGINS=https://a.sk,https://b.sk
 * Empty / unset = accept any origin (parent + sibling iframes).
 */
export function parseTokenHandshakeOrigins(raw: string | undefined): string[] {
	if (!raw?.trim()) return []

	return raw
		.split(',')
		.map(origin => origin.trim())
		.filter(Boolean)
}

/**
 * Trust check for JSON-RPC postMessage senders.
 * - same window always allowed (direct / console testing)
 * - immediate parent always allowed (Curo desktop WebView – often null/file origin)
 * - empty allowlist → any other sender (iframe→iframe OK)
 * - non-empty allowlist → only listed origins (Ambee)
 */
export function isTrustedHandshakeSender(
	event: Pick<MessageEvent, 'source' | 'origin'>,
	allowedOrigins: string[],
	currentWindow: Window = window
): boolean {
	if (event.source === currentWindow) return true
	// Desktop host (Curo): parent WebView, URL/origin often unknown or "null".
	if (event.source === currentWindow.parent) return true
	// Opaque / sandboxed sibling iframes (origin "null") are not trusted hosts.
	if (!event.origin || event.origin === 'null') return false
	if (allowedOrigins.length === 0) return true

	return allowedOrigins.includes(event.origin)
}

function clearStaleHandshakeAuth() {
	localStorage.removeItem('mdx_kz_user_token')
	localStorage.removeItem('mdx_kz_user_user')
	localStorage.removeItem('mdx_kz_current_access')
	localStorage.removeItem('mdx_kz_current_provider')
}

function beginTokenHandshake() {
	clearTokenHandshakeCompleted()
	sessionStorage.setItem(TOKEN_HANDSHAKE_SESSION_KEY, 'true')
	clearStaleHandshakeAuth()
}

/**
 * Must run before Pinia/router init.
 * Každý cold load iframe = nový handshake (vyčistiť starý JWT).
 * Výnimka: reload po zmene ambulancie.
 */
export function prepareTokenHandshakeSession() {
	if (!isHostIntegrationContext()) return

	if (sessionStorage.getItem(TOKEN_HANDSHAKE_ACCESS_SWITCH_KEY) === 'true') {
		sessionStorage.removeItem(TOKEN_HANDSHAKE_ACCESS_SWITCH_KEY)
		sessionStorage.setItem(TOKEN_HANDSHAKE_PRESERVE_SESSION_KEY, 'true')
		return
	}

	beginTokenHandshake()
}

export function resetHandshakeAuthStore(authStore: ReturnType<typeof useAuthStore>) {
	authStore.setToken()
	authStore.setUser()
	authStore.$patch({ authorization: null, status: '' })
}

// JSON-RPC cez postMessage: parent (Curo) vždy; sibling iframe len cez VITE_TOKEN_HANDSHAKE_ORIGINS.
export function setupTokenHandshake(router: Router) {
	const isHostIntegration = isHostIntegrationContext()
	const authStore = useAuthStore()
	const allowedOrigins = parseTokenHandshakeOrigins(
		import.meta.env.VITE_TOKEN_HANDSHAKE_ORIGINS
	)

	if (isHostIntegration && !consumePreservedHostSession()) {
		resetHandshakeAuthStore(authStore)
	}

	window.addEventListener('message', async (e: MessageEvent) => {
		if (!isTrustedHandshakeSender(e, allowedOrigins)) return

		const msg = e.data
		if (!msg || msg.jsonrpc !== '2.0' || !msg.method) return

		if (msg.method === 'logout') {
			clearTokenHandshakeCompleted()
			clearTokenHandshakeSession()
		} else if (msg.method === 'login') {
			beginTokenHandshake()
			resetHandshakeAuthStore(authStore)
		} else if (msg.method === 'gridcard') {
			sessionStorage.setItem(TOKEN_HANDSHAKE_SESSION_KEY, 'true')
		}

		const reply = (payload: JsonRpcPayload) => {
			if (!e.source || typeof (e.source as WindowProxy).postMessage !== 'function') return

			const targetOrigin = e.origin && e.origin !== 'null' ? e.origin : '*'

			;(e.source as WindowProxy).postMessage(
				{
					jsonrpc: '2.0',
					id: msg.id,
					...payload
				},
				targetOrigin
			)
		}

		try {
			const handler = tokenHandshakeHandlers[msg.method]

			if (!handler) {
				reply({
					error: { code: -32601, message: `Method not found: ${msg.method}` }
				})
				return
			}

			const payload = await handler(msg.params, { authStore, router })
			reply(payload)
		} catch (error: any) {
			reply({
				error: {
					code: -32603,
					message:
						error?.response?.data?.error ||
						error?.message ||
						'Internal error'
				}
			})
		}
	})
}
